import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES, Category } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Terminal, Cpu, Database, Shield, Zap, RefreshCw, 
  Layers, Lock, Globe, Server, Activity, ChevronRight, 
  Search, Radio, Eye, Crosshair, Sparkles, Move, Scale, ArrowLeft, Send, PlayCircle, BarChart2
} from 'lucide-react';

export function NexoraX3DEcosystem() {
  const categories27 = CATEGORIES.slice(0, 27);
  
  // Tab/view controls
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);
  
  // Orbit/Rotation angles
  const [rotAngle, setRotAngle] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startDragX = useRef(0);
  const startRotAngle = useRef(0);

  // Auto-rotation state
  const [autoRotate, setAutoRotate] = useState(true);

  // Subnode Interactive states
  const [activeSubnode, setActiveSubnode] = useState<string | null>(null);
  const [subnodeStats, setSubnodeStats] = useState<{[key: string]: {ping: number, load: number, status: string}}>({});
  const [logs, setLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] COGNITIVE CORE: NexoraX 27 Neural Grid initialized.`,
    `[${new Date().toLocaleTimeString()}] PERSPECTIVE ENGINE: 3D coordinates projected successfully.`
  ]);

  // Update auto-rotation angle
  useEffect(() => {
    if (!autoRotate || isDragging) return;
    const interval = setInterval(() => {
      setRotAngle(prev => (prev + 0.15) % 360);
    }, 16);
    return () => clearInterval(interval);
  }, [autoRotate, isDragging]);

  // Generate randomized realtime simulation metrics for subnodes when a category is selected
  useEffect(() => {
    if (!selectedCategory) return;
    const initialStats: {[key: string]: {ping: number, load: number, status: string}} = {};
    selectedCategory.nodes.forEach(node => {
      initialStats[node] = {
        ping: Math.floor(10 + Math.random() * 45), // 10ms to 55ms
        load: Math.floor(15 + Math.random() * 70), // 15% to 85%
        status: Math.random() > 0.05 ? 'ONLINE' : 'HIGH LOAD'
      };
    });
    setSubnodeStats(initialStats);
    setActiveSubnode(selectedCategory.nodes[0] || null);
    
    // Add telemetry log log
    addLog(`GRID ENGINE: Synchronized dynamic spectrum analyzer for node [${selectedCategory.title}].`);
  }, [selectedCategory]);

  // Simulate floating/fluctuating ping metrics
  useEffect(() => {
    if (!selectedCategory || !subnodeStats) return;
    const timer = setInterval(() => {
      setSubnodeStats(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(node => {
          const change = Math.floor((Math.random() - 0.5) * 8);
          let newPing = next[node].ping + change;
          if (newPing < 5) newPing = 5;
          if (newPing > 120) newPing = 120;
          
          let newLoad = next[node].load + Math.floor((Math.random() - 0.5) * 6);
          if (newLoad < 5) newLoad = 5;
          if (newLoad > 99) newLoad = 99;

          next[node] = {
            ...next[node],
            ping: newPing,
            load: newLoad,
            status: newLoad > 85 ? 'HIGH LOAD' : 'ONLINE'
          };
        });
        return next;
      });
    }, 1500);
    return () => clearInterval(timer);
  }, [selectedCategory, subnodeStats]);

  const addLog = (message: string) => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [`[${time}] ${message}`, ...prev.slice(0, 19)]);
  };

  // Drag handlers to spin the interactive 3D galaxy
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    startDragX.current = e.clientX;
    startRotAngle.current = rotAngle;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const deltaX = e.clientX - startDragX.current;
    // Rotate 1 degree per 3 pixels of drag
    setRotAngle(startRotAngle.current + deltaX * 0.3);
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  const filteredCategories = categories27.filter(cat => 
    cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.nodes.some(node => node.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Colors for the 3 distinct orbital bands
  const bandColors = [
    { border: 'border-cyan-500/20', line: 'stroke-cyan-500/10', glow: 'shadow-[0_0_15px_rgba(34,211,238,0.15)]' },
    { border: 'border-[#e840f3]/20', line: 'stroke-[#e840f3]/10', glow: 'shadow-[0_0_15px_rgba(232,64,243,0.15)]' },
    { border: 'border-amber-500/20', line: 'stroke-amber-500/10', glow: 'shadow-[0_0_15px_rgba(245,158,11,0.15)]' }
  ];

  return (
    <div id="NexoraX-3d-ecosystem-root" className="w-full text-left space-y-8 select-none font-sans">
      
      {/* HEADER CONTROLS & COMPASS */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-black/60 border border-white/5 p-5 rounded-2xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00ffd2] animate-pulse" />
            <h3 className="text-sm font-mono text-[#00ffd2] uppercase tracking-[0.25em] font-extrabold">NexoraX Galaxy Mesh Navigation</h3>
          </div>
          <p className="text-slate-400 text-xs font-mono">
            Centering 27 celestial sectors mapped inside a responsive 3D viewport. Drag to rotate gravity orbits.
          </p>
        </div>

        {/* CONTROLS */}
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          {/* Active Search Filter */}
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Filter galaxy sectors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 bg-zinc-950 border border-zinc-800 focus:border-cyan-400 font-mono text-[10px] text-white rounded-xl pl-9 pr-4 py-2 placeholder-zinc-700 outline-none transition-colors"
            />
          </div>

          <button
            onClick={() => setAutoRotate(!autoRotate)}
            className={`px-3.5 py-2 font-mono text-[9px] font-bold rounded-xl border uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
              autoRotate 
                ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' 
                : 'bg-zinc-900 border-zinc-800 text-zinc-500'
            }`}
          >
            <RefreshCw size={10} className={autoRotate ? 'animate-spin' : ''} />
            <span>Auto-Rotate: {autoRotate ? 'ON' : 'OFF'}</span>
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!selectedCategory ? (
          <motion.div
            key="galaxy-mesh-view"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 xl:grid-cols-3 gap-6"
          >
            
            {/* VIEWPORT AREA AND 3D GRID */}
            <div 
              className="xl:col-span-2 min-h-[500px] h-[550px] relative bg-gradient-to-b from-[#030612]/90 to-black rounded-3xl border border-indigo-500/15 overflow-hidden flex flex-col justify-between p-6 cursor-grab active:cursor-grabbing shadow-inner"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUpOrLeave}
              onMouseLeave={handleMouseUpOrLeave}
            >
              
              {/* Star Background Matrix Decoration */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-950/20 via-black to-black pointer-events-none opacity-80" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] bg-gradient-to-tr from-cyan-500/5 to-indigo-500/5 rounded-full blur-[80px] pointer-events-none" />

              {/* HUD Header overlay labels */}
              <div className="flex justify-between items-start relative z-10 w-full select-none pointer-events-none">
                <div className="font-mono text-[9px] text-[#e840f3] bg-[#e840f3]/10 border border-[#e840f3]/25 px-2 py-1 rounded">
                  VIEWPORT DETECTOR: ACTIVE PERSPECTIVE
                </div>
                <div className="font-mono text-[9px] text-zinc-500 text-right">
                  DEVIATION ROT: {rotAngle.toFixed(1)}° <br/>
                  SENSING NODES: 27 SECTORS
                </div>
              </div>

              {/* CORE INTERACTIVE 3D ORBIT AREA */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-[500px] h-[500px] flex items-center justify-center transform-style-preserve-3d">
                  
                  {/* Outer Gravitational Trace Ring 3 */}
                  <div className="absolute w-[440px] h-[440px] border border-cyan-500/10 rounded-full select-none transition-transform pointer-events-none" style={{ transform: 'rotateX(72deg) rotateY(15deg)' }} />
                  {/* Middle Gravitational Trace Ring 2 */}
                  <div className="absolute w-[320px] h-[320px] border border-[#e840f3]/10 rounded-full select-none transition-transform pointer-events-none" style={{ transform: 'rotateX(72deg) rotateY(-10deg)' }} />
                  {/* Inner Gravitational Trace Ring 1 */}
                  <div className="absolute w-[200px] h-[200px] border border-amber-500/10 rounded-full select-none transition-transform pointer-events-none" style={{ transform: 'rotateX(72deg) rotateY(5deg)' }} />

                  {/* CENTRAL MASTER COGNITIVE ENGINE SOURCE */}
                  <div className="absolute z-20 pointer-events-auto flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-cyan-950 to-indigo-950 border-2 border-cyan-500/40 shadow-[0_0_30px_rgba(6,182,212,0.4)] flex items-center justify-center relative animate-pulse group cursor-help"
                      title="NEXORAX NEXORAX CENTRAL NODE"
                      onClick={() => addLog("SYSTEM: Query directed to secure primary master database router.")}
                    >
                      <Database className="w-6 h-6 text-[#00ffd2] animate-bounce" />
                      <div className="absolute inset-[-10px] rounded-full border border-indigo-500/25 animate-ping" />
                    </div>
                    <span className="text-[8px] font-mono text-cyan-400 font-extrabold mt-2 tracking-widest bg-zinc-950/80 px-2 py-0.5 rounded border border-cyan-500/25">
                      NEXORAX CORE OS
                    </span>
                  </div>

                  {/* CATEGORIES SPACE COORD GENERATION */}
                  {filteredCategories.map((cat, idx) => {
                    // Mapped to 3 concentric orbits
                    const orbitTier = idx % 3;
                    const radius = orbitTier === 0 ? 100 : orbitTier === 1 ? 160 : 220;
                    
                    // Space coordinate index placement (3D projection)
                    const baseAngle = (idx * (360 / filteredCategories.length)) + rotAngle;
                    const radian = (baseAngle * Math.PI) / 180;
                    
                    // Elliptical perspective projecting 72-degree X tilt
                    const x = Math.cos(radian) * radius;
                    const y = Math.sin(radian) * radius * 0.35; // Squashed vertical axis to project tilted orbit 
                    const z = Math.sin(radian) * radius * 0.9;  // Depth variable to scale and shift visual hierarchy
                    
                    // Normalize depths to compute zIndex & opacity
                    const isBack = z < 0;
                    const depthZIndex = isBack ? 10 : 30;
                    const scaleFactor = 0.75 + ((z + radius) / (radius * 2)) * 0.45;

                    const colorGroup = bandColors[orbitTier];
                    const isHovered = hoveredCategory?.id === cat.id;

                    return (
                      <div
                        key={cat.id}
                        className="absolute pointer-events-auto transition-transform duration-75"
                        style={{
                          transform: `translate(${x}px, ${y}px) scale(${scaleFactor})`,
                          zIndex: depthZIndex,
                        }}
                      >
                        <div
                          onMouseEnter={() => setHoveredCategory(cat)}
                          onMouseLeave={() => setHoveredCategory(null)}
                          onClick={() => {
                            setSelectedCategory(cat);
                            setHoveredCategory(null);
                          }}
                          className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-zinc-950/90 border-2 flex items-center justify-center cursor-pointer transition-all ${
                            isHovered 
                              ? 'border-cyan-400 bg-cyan-900/35 scale-110 shadow-[0_0_20px_rgba(34,211,238,0.7)]' 
                              : `border-zinc-800 hover:border-zinc-500 ${colorGroup.glow}`
                          }`}
                        >
                          <span className="text-[10px] font-black font-sans text-white tracking-tighter">
                            {cat.id}
                          </span>
                        </div>

                        {/* Interactive floating banner overlay */}
                        {(isHovered || isDragging) && (
                          <div className="absolute top-[110%] left-1/2 -translate-x-1/2 bg-[#02040b]/90 border border-zinc-800 p-2 rounded-xl text-center min-w-[124px] pointer-events-none shadow-2xl z-50">
                            <div className="text-[7.5px] font-mono font-bold text-zinc-500 uppercase">SECTOR {cat.id}</div>
                            <div className="text-[9px] font-black text-slate-100 truncate w-full uppercase tracking-tight">{cat.title}</div>
                            <div className="text-[7px] font-mono text-emerald-400 mt-0.5 uppercase tracking-widest">{cat.nodes.length} Endpoints</div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                </div>
              </div>

              {/* Viewport footer hint helper */}
              <div className="relative z-10 font-mono text-[8px] text-zinc-500 flex justify-between select-none pointer-events-none">
                <div className="flex items-center gap-1.5 text-zinc-400">
                  <Move size={9} className="text-zinc-500" />
                  <span>Interactive Drag Active // Spin Left-Right</span>
                </div>
                <div className="text-right text-[#00ffd2] animate-pulse">
                  STABILIZATION LATENCY: 0.8S STANDARD SECURED
                </div>
              </div>

            </div>

            {/* SECTOR LISTING GRID INDEX MAP (RIGHT PANEL) */}
            <div className="flex flex-col gap-4 bg-[#020408]/90 border border-zinc-800 rounded-3xl p-5 shadow-2xl relative overflow-hidden max-h-[550px]">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
              
              <div className="border-b border-zinc-900 pb-3 flex justify-between items-center shrink-0">
                <h3 className="text-xs font-mono font-black uppercase text-indigo-400 flex items-center gap-1.5">
                  <Layers size={11} />
                  <span>NexoraX Sector Matrix (1-27)</span>
                </h3>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-zinc-900 border border-zinc-800 text-slate-300 rounded">
                  {filteredCategories.length} / 27
                </span>
              </div>

              {/* Scrollable list items */}
              <div className="flex-1 overflow-y-auto pr-1 space-y-2 select-none">
                {filteredCategories.length === 0 ? (
                  <div className="text-center py-12 text-zinc-600 font-mono text-xs">
                    No sectors match current query filter.
                  </div>
                ) : (
                  filteredCategories.map((cat) => {
                    const isHovered = hoveredCategory?.id === cat.id;
                    return (
                      <div
                        key={cat.id}
                        onMouseEnter={() => setHoveredCategory(cat)}
                        onMouseLeave={() => setHoveredCategory(null)}
                        onClick={() => setSelectedCategory(cat)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex justify-between items-center text-left ${
                          isHovered 
                            ? 'bg-zinc-900/80 border-cyan-500/45' 
                            : 'bg-zinc-950/60 border-zinc-900 hover:border-zinc-800'
                        }`}
                      >
                        <div className="space-y-1 w-4/5 select-none">
                          <div className="flex items-center gap-2">
                            <span className="text-[8px] font-mono font-extrabold px-1.5 py-0.2 bg-zinc-900 text-cyan-400 border border-zinc-800 rounded">
                              SEC-{cat.id}
                            </span>
                            <span className="text-[10px] text-white font-bold uppercase truncate tracking-tight">{cat.title}</span>
                          </div>
                          <p className="text-[8.5px] font-mono text-zinc-500 truncate">
                            {cat.nodes.slice(0, 3).join(" • ")}...
                          </p>
                        </div>
                        <ChevronRight size={12} className={`text-zinc-600 ${isHovered ? 'text-cyan-400 translate-x-1' : ''} transition-transform`} />
                      </div>
                    );
                  })
                )}
              </div>

              {/* Dynamic Console Logger Output Footer */}
              <div className="bg-black/90 rounded-2xl p-3 border border-zinc-900 font-mono text-[8.5px] leading-relaxed text-zinc-400 space-y-1 shrink-0 h-[100px] overflow-hidden">
                <div className="text-[7.5px] uppercase font-bold text-slate-500 flex justify-between">
                  <span>Sensory Telemetry Ledger Monitor</span>
                  <span className="text-[#00ffd2]">SYSTEM ONLINE</span>
                </div>
                <div className="space-y-0.5 overflow-hidden h-[74px]">
                  {logs.slice(0, 5).map((l, i) => (
                    <div key={i} className="truncate text-zinc-500">
                      <span className="text-zinc-600">➔</span> {l}
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </motion.div>
        ) : (
          <motion.div
            key="sector-drilldown-inspector"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="w-full text-left"
          >
            {/* INSPECTOR DRILLDOWN COMPONENT MAP */}
            <div className="bg-gradient-to-b from-zinc-950 to-black border-2 border-indigo-500/20 rounded-3xl p-6 lg:p-8 space-y-6 shadow-2xl relative">
              <div className="absolute -top-12 -right-12 p-12 opacity-[0.02] pointer-events-none">
                <PulseGridBG />
              </div>

              {/* Header Action Nav */}
              <div className="space-y-4 border-b border-zinc-900 pb-5">
                {/* Micro Breadcrumb Trail */}
                <div id="NexoraX-breadcrumb-trail" className="flex flex-wrap items-center gap-2 text-[10px] font-mono text-zinc-500 select-none">
                  <span 
                    onClick={() => {
                      setSelectedCategory(null);
                      setActiveSubnode(null);
                      triggerToast("Returned to NexoraX Galaxy View", "info");
                    }}
                    className="hover:text-cyan-400 hover:underline cursor-pointer transition-colors font-extrabold uppercase tracking-wider flex items-center gap-1"
                  >
                    🌌 NexoraX Hub
                  </span>
                  <ChevronRight size={10} className="text-zinc-750" />
                  <span className="text-zinc-600 uppercase font-medium tracking-wider">Sector Groups</span>
                  <ChevronRight size={10} className="text-zinc-750" />
                  <span 
                    onClick={() => {
                      setActiveSubnode(null);
                      triggerToast(`Focused on Sector ${selectedCategory.id}`, "info");
                    }}
                    className={`uppercase tracking-wider font-extrabold transition-colors ${
                      activeSubnode ? 'hover:text-cyan-400 hover:underline cursor-pointer text-zinc-400' : 'text-cyan-400'
                    }`}
                  >
                    Sector {selectedCategory.id}: {selectedCategory.title}
                  </span>
                  {activeSubnode && (
                    <>
                      <ChevronRight size={10} className="text-zinc-750" />
                      <span className="text-[#e840f3] font-black uppercase tracking-wider bg-[#e840f3]/10 px-1.5 py-0.5 rounded border border-[#e840f3]/20 truncate max-w-[150px] sm:max-w-[250px]" title={activeSubnode}>
                        🎯 {activeSubnode}
                      </span>
                    </>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-1">
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setActiveSubnode(null);
                    }}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 text-[10px] font-mono font-bold uppercase rounded-xl tracking-widest transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <ArrowLeft size={11} />
                    <span>Back to Galaxy Perspective</span>
                  </button>

                  <div className="flex items-center gap-2 font-mono text-[9px] bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 rounded-xl text-cyan-400">
                    <Radio size={11} className="animate-pulse" />
                    <span>DRILLDOWN TELEMETRY MODE ACTIVE for SEC-{selectedCategory.id}</span>
                  </div>
                </div>
              </div>

              {/* Master Title Frame */}
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-5">
                <div className="space-y-1">
                  <span className="text-[#e840f3] font-mono text-[9px] uppercase tracking-[0.3em] font-black">
                    NEXORAX SYSTEMS DIRECTORY MATRIX #0{selectedCategory.id}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-sans font-black uppercase text-white tracking-tight leading-tight">
                    {selectedCategory.title} Spectrum Controls
                  </h2>
                  <p className="text-[10px] sm:text-xs text-zinc-400 max-w-2xl font-mono leading-relaxed">
                    Executing dynamic telemetry pipelines, compliance filters and audit loops directly synced under corporate entity SC887095 protocols and permanent Secure Payment Provider settlement paths.
                  </p>
                </div>

                {/* Micro KPI Widgets */}
                <div className="grid grid-cols-2 gap-3.5 bg-black border border-zinc-900 p-4 rounded-xl w-full lg:w-auto">
                  <div>
                    <span className="text-zinc-600 font-mono text-[7px] block uppercase tracking-wider font-extrabold">Active Nodes</span>
                    <span className="text-white font-mono text-base font-black">
                      {selectedCategory.nodes.length} / {selectedCategory.nodes.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-600 font-mono text-[7px] block uppercase tracking-wider font-extrabold">Aggregate Load</span>
                    <span className="text-[#00ffd2] font-mono text-base font-black">
                      {(() => {
                        const keys = Object.keys(subnodeStats);
                        if (keys.length === 0) return "0.0";
                        const sum = keys.reduce((acc, k) => acc + (subnodeStats[k]?.load || 0), 0);
                        return (sum / keys.length).toFixed(1);
                      })()}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Dual Column Workspace Detail layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* 1. Subnode Selection list */}
                <div className="lg:col-span-1 border border-zinc-900 bg-black/60 rounded-2xl p-4 flex flex-col max-h-[460px]">
                  <div className="text-[9px] uppercase font-mono font-bold text-zinc-500 block mb-2 px-1">Subnode Enclave Index</div>
                  
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 font-mono text-[10px]">
                    {selectedCategory.nodes.map((node) => {
                      const stats = subnodeStats[node] || { ping: 22, load: 30, status: 'ONLINE' };
                      const isSubActive = activeSubnode === node;

                      return (
                        <div
                          key={node}
                          onClick={() => {
                            setActiveSubnode(node);
                            addLog(`VISUALIZER: Active subnode focused to [${node.toUpperCase()}].`);
                          }}
                          className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                            isSubActive 
                              ? 'bg-zinc-900 border-indigo-500/50 text-white' 
                              : 'bg-zinc-950/40 border-zinc-900 text-slate-400 hover:text-white'
                          }`}
                        >
                          <div className="flex justify-between items-center gap-1">
                            <span className="font-bold truncate max-w-[150px]">{node}</span>
                            <span className={`text-[8px] px-1.5 rounded-md py-0.2 select-none font-bold ${
                              stats.status === 'ONLINE' ? 'bg-emerald-950/30 text-emerald-400 border border-emerald-500/20' : 'bg-red-950/30 text-red-400 border border-red-500/20'
                            }`}>
                              {stats.ping}ms
                            </span>
                          </div>
                          
                          {/* Mini Progress loading value trace */}
                          <div className="w-full h-1 bg-zinc-950 rounded-full mt-2.5 overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${stats.load > 85 ? 'bg-red-500' : isSubActive ? 'bg-indigo-400' : 'bg-cyan-500'}`}
                              style={{ width: `${stats.load}%` }} 
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Focused Telemetry Area */}
                <div className="lg:col-span-2 border border-zinc-900 bg-[#020308]/90 p-5 rounded-2xl flex flex-col justify-between space-y-5">
                  {activeSubnode ? (
                    <div className="space-y-4">
                      <div className="flex justify-between items-start gap-4">
                        <div className="space-y-1">
                          <span className="text-[8px] font-mono font-bold text-cyan-400 uppercase tracking-widest block">TARGET ENDPOINT SENSOR DETECTOR</span>
                          <h4 className="text-sm font-sans font-black text-slate-100 uppercase tracking-tight">{activeSubnode}</h4>
                        </div>
                        <div className="font-mono text-[9px] text-right">
                          <span className="text-zinc-500">Node Status:</span> <strong className="text-emerald-400 ml-1">SECURED AND BALANCED</strong> <br/>
                          <span className="text-zinc-500">Assigned Agent:</span> <strong className="text-[#e840f3] ml-1">ANTIGRAVITY-05-2026</strong>
                        </div>
                      </div>

                      {/* Interactive Graph simulation inside subnode */}
                      <div className="bg-black/80 rounded-2xl border border-zinc-900 p-4 h-[180px] flex flex-col justify-between relative overflow-hidden">
                        <div className="absolute top-2 right-2 flex items-center gap-2 text-[8px] font-mono text-zinc-500 uppercase">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00ffd2] animate-ping" />
                          <span>0.8s Sensory Response Loop</span>
                        </div>

                        {/* Visual wave projection representing simulated line chart */}
                        <div className="flex-1 flex items-end gap-[3px] sm:gap-[5px] select-none h-[110px] pt-4">
                          {Array.from({ length: 28 }).map((_, i) => {
                            const statsVal = subnodeStats[activeSubnode] || { ping: 25, load: 35 };
                            const heightPercentage = Math.sin((i / 4.4) + (statsVal.ping / 15)) * 30 + (statsVal.load * 0.45) + 30;
                            return (
                              <div 
                                key={i} 
                                className={`flex-1 rounded-sm transition-all duration-300 ${
                                  i === 27 ? 'bg-cyan-400 border border-cyan-300' : 'bg-gradient-to-t from-indigo-950 to-indigo-500/40'
                                }`}
                                style={{ height: `${Math.max(10, Math.min(100, heightPercentage))}%` }}
                              />
                            );
                          })}
                        </div>

                        <div className="flex justify-between items-center text-[8.5px] font-mono text-zinc-500 uppercase pt-2 border-t border-zinc-900">
                          <span>SYSTEM SHARDS: STREAMS DETECTED</span>
                          <span>LATENCY THRESHOLD: 45ms</span>
                          <span>CORE METRIC: {subnodeStats[activeSubnode]?.ping || 24}ms</span>
                        </div>
                      </div>

                      {/* Action trigger deck inside detailed telemetry node */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                          onClick={() => {
                            const nextStats = { ...subnodeStats };
                            if (activeSubnode && nextStats[activeSubnode]) {
                              nextStats[activeSubnode] = {
                                ...nextStats[activeSubnode],
                                ping: Math.max(5, nextStats[activeSubnode].ping - 12),
                                load: Math.max(10, nextStats[activeSubnode].load - 15)
                              };
                              setSubnodeStats(nextStats);
                              triggerToast("Triggered Cloudflare Node Shield Compression!", "success");
                              addLog(`CLOUD SHIELD: Flushed traffic routes on ${activeSubnode.toUpperCase()}. Latency reduced.`);
                            }
                          }}
                          className="p-2.5 bg-zinc-900/90 hover:bg-zinc-800 rounded-xl border border-zinc-800 hover:border-cyan-500/30 text-[9px] font-mono font-black uppercase text-center transition-colors text-cyan-400 cursor-pointer flex items-center justify-center gap-1.5"
                        >
                          <Shield size={11} />
                          <span>Trigger Shield Null</span>
                        </button>

                        <button
                          onClick={() => {
                            triggerToast("Provisioned secondary auto-failover route!", "info");
                            addLog(`ROUTING: Mapped fallback IP node route for ${activeSubnode.toUpperCase()}.`);
                          }}
                          className="p-2.5 bg-zinc-900/90 hover:bg-zinc-800 rounded-xl border border-zinc-800 hover:border-indigo-500/30 text-[9px] font-mono font-black uppercase text-center transition-colors text-indigo-400 cursor-pointer flex items-center justify-center gap-1.5"
                        >
                          <RefreshCw size={11} className="animate-spin" />
                          <span>Failover Shard</span>
                        </button>

                        <button
                          onClick={() => {
                            triggerToast(`Auditing SC887095 schema on ${activeSubnode}`, "success");
                            addLog(`AUDIT: Dispatched GovTalk compliance certificate to ${activeSubnode.toUpperCase()}.`);
                          }}
                          className="p-2.5 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 hover:from-teal-500/20 hover:to-emerald-500/20 rounded-xl border border-emerald-500/20 text-[9px] font-mono font-black uppercase text-center transition-all text-emerald-400 cursor-pointer flex items-center justify-center gap-1.5"
                        >
                          <Scale size={11} />
                          <span>Audit GovTalk</span>
                        </button>
                      </div>

                    </div>
                  ) : (
                    <div className="text-center py-20 text-zinc-600 font-mono text-xs">
                      Select a subnode on the left to activate focused sensory telemetries.
                    </div>
                  )}

                  {/* Multi-stage Tax accounting tracker bottom alert */}
                  <div className="p-3 bg-zinc-950/90 border border-zinc-900 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3.5 text-xs text-zinc-400 font-mono">
                    <div className="space-y-1">
                      <div className="text-[7.5px] uppercase font-bold text-zinc-500 tracking-wider">NEXORAX LEDGER FINANCIAL ANCHOR DETECTOR</div>
                      <div>
                        Account Integrated: Secure Payment Provider Sort Code: 04-06-05 Account: 31396515 (Fidelity Locked)
                      </div>
                    </div>
                    <div className="flex gap-4 sm:shrink-0 text-right">
                      <div>
                        <span className="text-[7.5px] uppercase text-cyan-400 block font-bold">1% Perpetual Levy</span>
                        <span className="text-cyan-400 font-extrabold text-[10px]">COV: £28,200.00</span>
                      </div>
                      <div>
                        <span className="text-[7.5px] uppercase text-[#e840f3] block font-bold">10% Maintenance & Hosting Fee</span>
                        <span className="text-[#e840f3] font-extrabold text-[10px]">TAX: £564,000.00</span>
                      </div>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

// Custom Toast Trigger helper
const triggerToast = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
  window.dispatchEvent(new CustomEvent('nexorax-trigger-toast', {
    detail: { message: msg, type }
  }));
};

// SVG Background helper
function PulseGridBG() {
  return (
    <svg width="450" height="420" viewBox="0 0 450 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-pulse">
      <defs>
        <pattern id="grid-pattern-drill" width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M 18 0 L 0 0 0 18" fill="none" stroke="rgba(99,102,241,0.06)" strokeWidth="0.8"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid-pattern-drill)"/>
    </svg>
  );
}
